import {
  conectarBotonesAgregar,
  escaparHtml,
  enlaceWhatsApp,
  tarjetaProductoHtml,
} from '../componentes.js';
import { iconoLucide } from '../iconos.js';
import { SECCIONES_PRODUCTOS, clasesBotones, resolverVarianteCategorias } from '../motortema.js';
import { iconoRedSocial } from '../redes-sociales.js';

function enlaceBanner(banner) {
  if (!banner?.enlace_tipo || !banner?.enlace_valor) return '/catalogo';
  if (banner.enlace_tipo === 'producto') return `/producto/${banner.enlace_valor}`;
  if (banner.enlace_tipo === 'categoria') return `/catalogo?categoria=${banner.enlace_valor}`;
  if (banner.enlace_tipo === 'url') return banner.enlace_valor;
  return '/catalogo';
}

function bannersDelBloque(tienda, bloque) {
  const ids = bloque.configuracion?.ids_banner ?? [];
  return tienda.banners.filter((banner) => ids.includes(banner.id));
}

function bloqueBanner(tienda, bloque, estado) {
  const banners = bannersDelBloque(tienda, bloque);
  if (banners.length === 0) return '';

  const bannerPres = estado?.editor?.banner ?? {};
  const autoplay = bannerPres.autoplay !== false;
  // La altura vive en unidades de viewport (`svh` = altura "pequeña"
  // estable, sin la barra móvil dinámica). Las variantes de cabecera tienen
  // alturas distintas, así que el valor se combina con `--sp-header-h` para
  // llenar la pantalla visible bajo el header sin forzar un 100vh ciego.
  const altura =
    bloque.configuracion?.altura ?? 'min(88svh, calc(100svh - var(--sp-header-h, 68px)))';
  const overlay = Math.min(0.78, Math.max(0.1, Number(bannerPres.overlay ?? 54) / 100)).toFixed(2);
  const alineacion = ['left', 'center', 'right'].includes(bannerPres.alineacion)
    ? bannerPres.alineacion
    : 'left';
  const etiqueta = bannerPres.etiqueta ?? tienda.empresa.nombre;
  const textoBoton = bannerPres.texto_boton || 'Ver catálogo';

  const contenidoDeBanner = (banner) => `
      <div class="sp-contenedor sp-hero__contenido">
        <div class="sp-hero__texto">
          <p class="sp-eyebrow">${escaparHtml(etiqueta)}</p>
          <h1>${escaparHtml(banner.titulo ?? tienda.empresa.nombre)}</h1>
          ${banner.subtitulo ? `<p>${escaparHtml(banner.subtitulo)}</p>` : ''}
          <div class="sp-acciones sp-acciones--hero">
            <a class="${clasesBotones(estado, 'sp-boton--primario sp-boton--grande')}" href="${escaparHtml(enlaceBanner(banner))}">
              ${iconoLucide('ShoppingBag')}<span>${escaparHtml(textoBoton)}</span>
            </a>
            <a class="sp-boton sp-boton--secundario sp-boton--grande" href="${enlaceWhatsApp(tienda)}" target="_blank" rel="noreferrer">
              ${iconoRedSocial('whatsapp')}<span>Consultar</span>
            </a>
          </div>
        </div>
      </div>`;

  return `
    <section
      class="sp-hero sp-hero--${alineacion}"
      data-sp-hero
      data-autoplay="${autoplay ? 'true' : 'false'}"
      style="--sp-hero-altura:${escaparHtml(altura)};--sp-hero-overlay:${overlay};"
    >
      <div class="sp-hero__slides">
        ${banners
          .map(
            (banner, index) => `
              <article class="sp-hero__slide ${index === 0 ? 'sp-hero__slide--activa' : ''}" data-sp-slide>
                <picture>
                  ${
                    banner.imagen_movil_url
                      ? `<source media="(max-width: 640px)" srcset="${escaparHtml(banner.imagen_movil_url)}" />`
                      : ''
                  }
                  <img src="${escaparHtml(banner.imagen_url)}" alt="${escaparHtml(banner.titulo ?? tienda.empresa.nombre)}" ${index === 0 ? 'fetchpriority="high"' : 'loading="lazy"'} decoding="async" />
                </picture>
                <div class="sp-hero__overlay" aria-hidden="true"></div>
                ${contenidoDeBanner(banner)}
              </article>
            `,
          )
          .join('')}
      </div>
      ${
        banners.length > 1
          ? `<div class="sp-hero__controles" aria-label="Controles del banner">
              <button class="sp-icon-button" type="button" data-sp-hero-prev aria-label="Banner anterior">${iconoLucide('ChevronLeft')}</button>
              <div class="sp-hero__indicadores">
                ${banners
                  .map(
                    (_, index) =>
                      `<button type="button" class="${index === 0 ? 'sp-hero__indicador--activo' : ''}" data-sp-hero-ir="${index}" aria-label="Ver banner ${index + 1}"></button>`,
                  )
                  .join('')}
              </div>
              <button class="sp-icon-button" type="button" data-sp-hero-next aria-label="Banner siguiente">${iconoLucide('ChevronRight')}</button>
            </div>`
          : ''
      }
    </section>
  `;
}

function conectarSliders(contenedor, reducirMovimiento = false) {
  contenedor.querySelectorAll('[data-sp-hero]').forEach((hero) => {
    const slides = [...hero.querySelectorAll('[data-sp-slide]')];
    if (slides.length <= 1) return;

    const indicadores = [...hero.querySelectorAll('[data-sp-hero-ir]')];
    let actual = 0;
    let temporizador = null;

    const mostrar = (indice) => {
      actual = (indice + slides.length) % slides.length;
      slides.forEach((slide, i) => slide.classList.toggle('sp-hero__slide--activa', i === actual));
      indicadores.forEach((boton, i) =>
        boton.classList.toggle('sp-hero__indicador--activo', i === actual),
      );
    };

    const reiniciar = () => {
      if (temporizador) window.clearInterval(temporizador);
      if (!reducirMovimiento && hero.dataset.autoplay === 'true') {
        temporizador = window.setInterval(() => mostrar(actual + 1), 6200);
      }
    };

    hero.querySelector('[data-sp-hero-prev]')?.addEventListener('click', () => {
      mostrar(actual - 1);
      reiniciar();
    });
    hero.querySelector('[data-sp-hero-next]')?.addEventListener('click', () => {
      mostrar(actual + 1);
      reiniciar();
    });
    indicadores.forEach((boton) => {
      boton.addEventListener('click', () => {
        mostrar(Number(boton.dataset.spHeroIr));
        reiniciar();
      });
    });

    reiniciar();
  });
}

function tarjetaCategoria(categoria, modificador = '') {
  return `
    <a class="sp-categoria${modificador ? ` ${modificador}` : ''}" href="/catalogo?categoria=${escaparHtml(categoria.slug)}" data-categoria-fallback="${categoria.imagen_url ? 'no' : 'si'}">
      <div class="sp-categoria__media">
        ${
          categoria.imagen_url
            ? `<img src="${escaparHtml(categoria.imagen_url)}" alt="${escaparHtml(categoria.nombre)}" loading="lazy" decoding="async" />`
            : `<span class="sp-categoria__fallback" aria-hidden="true">${iconoLucide('Image')}</span>`
        }
        <span class="sp-categoria__sombra" aria-hidden="true"></span>
      </div>
      <span class="sp-categoria__etiqueta">
        <strong>${escaparHtml(categoria.nombre)}</strong>
        ${categoria.descripcion ? `<small>${escaparHtml(categoria.descripcion)}</small>` : ''}
      </span>
    </a>`;
}

function bloqueCategorias(tienda, bloque, estado) {
  const ids = bloque.configuracion?.ids_categoria ?? [];
  const categorias = tienda.categorias
    .filter((categoria) => ids.includes(categoria.id))
    .slice(0, 6);
  if (categorias.length === 0) return '';

  // La variante de composición llega del editor (espejo de HEADER):
  // EDITOR = PREVIEW = STOREFRONT. Si la tienda no la configuró, cae a
  // clásico (ver resolverVarianteCategorias).
  const variante = resolverVarianteCategorias(estado?.editor);
  const jerarquia = variante.caracteristicas.protagonista;

  const tarjetas = categorias
    .map((categoria, indice) => {
      // En las variantes con jerarquía, la primera tarjeta es la
      // protagonista y la segunda una secundaria; el resto compone el grid.
      const modificadores = [];
      if (jerarquia && indice === 0) modificadores.push('sp-categoria--protagonista');
      else if (jerarquia && indice === 1 && categorias.length > 1)
        modificadores.push('sp-categoria--secundaria');
      return tarjetaCategoria(categoria, modificadores.join(' '));
    })
    .join('');

  return `
    <section class="sp-seccion" data-categorias>
      <div class="sp-contenedor">
        <div class="sp-seccion__cabecera">
          <div>
            <p class="sp-eyebrow">Explora</p>
            <h2>Categorías destacadas</h2>
          </div>
          <a class="sp-seccion__ver-todo" href="/catalogo">Ver todo</a>
        </div>
        <div class="sp-categorias sp-categorias--${escaparHtml(variante.id)} sp-categorias--n${Math.min(categorias.length, 4)}" data-variante="${escaparHtml(variante.id)}">
          ${tarjetas}
        </div>
      </div>
    </section>
  `;
}

function bloquePropuestaValor(tienda, bloque, estado) {
  const items = bloque.configuracion?.items ?? [];
  if (items.length === 0) return '';
  const pres = estado?.editor?.propuesta_valor ?? {};
  const variante = pres.variante || 'banda';

  const itemHtml = (item, extra) => `
    <div class="sp-confianza__item${extra ? ` ${extra}` : ''}">
      <span class="sp-confianza__icono" aria-hidden="true">${iconoLucide('Star')}</span>
      <strong>${escaparHtml(item.titulo)}</strong>
      <span>${escaparHtml(item.descripcion)}</span>
    </div>`;
  const visibles = items.slice(0, 4);

  let interior;
  if (variante === 'tarjetas') {
    interior = `
      <div class="sp-propuesta__rejilla">
        ${visibles.map((item) => itemHtml(item, 'sp-confianza__item--tarjeta')).join('')}
      </div>`;
  } else if (variante === 'editorial') {
    interior = `
      <div class="sp-contenedor sp-propuesta__editorial">
        <div class="sp-propuesta__editorial-titulo">
          <p class="sp-eyebrow">Beneficios</p>
          <h2>${escaparHtml(pres.titulo || '¿Por qué comprar aquí?')}</h2>
        </div>
        <div class="sp-propuesta__rejilla sp-propuesta__rejilla--2col">
          ${visibles.map((item) => itemHtml(item)).join('')}
        </div>
      </div>`;
  } else if (variante === 'minimal') {
    interior = `
      <div class="sp-contenedor sp-propuesta__minimal">
        ${visibles.map((item) => itemHtml(item, 'sp-confianza__item--minimal')).join('')}
      </div>`;
  } else {
    interior = `
      <div class="sp-contenedor sp-confianza">
        ${visibles.map((item) => itemHtml(item)).join('')}
      </div>`;
  }

  return `\n    <section class="sp-franja-confianza sp-franja-confianza--${variante}">\n      ${interior}\n    </section>`;
}

const ETIQUETAS_PRODUCTOS = {
  productos_destacados: { eyebrow: 'Selección', titulo: 'Productos destacados' },
  productos_nuevos: { eyebrow: 'Recién llegados', titulo: 'Productos nuevos' },
  promociones: { eyebrow: 'Ahorra hoy', titulo: 'Promociones' },
  novedades: { eyebrow: 'Novedades', titulo: 'Novedades' },
};

function filtroDeSeccion(tipo) {
  if (tipo === 'productos_destacados') return (producto) => Boolean(producto.es_destacado);
  if (tipo === 'productos_nuevos' || tipo === 'novedades')
    return (producto) => Boolean(producto.es_nuevo);
  if (tipo === 'promociones') return (producto) => Boolean(producto.es_oferta);
  return () => true;
}

function bloqueProductos(tienda, bloque, tipo, estado) {
  if (!SECCIONES_PRODUCTOS.has(tipo)) return '';
  const cantidad = bloque.configuracion?.cantidad ?? 8;
  const coincide = filtroDeSeccion(tipo);
  const seleccion = tienda.productos.filter((producto) => coincide(producto)).slice(0, cantidad);
  if (seleccion.length === 0) return '';

  const pres = estado?.editor?.productos ?? {};
  const variante = pres.variante || 'clasico';
  const etiquetas = ETIQUETAS_PRODUCTOS[tipo] ?? ETIQUETAS_PRODUCTOS.productos_destacados;
  const titulo = pres.titulo || etiquetas.titulo;
  const eyebrow = pres.eyebrow || etiquetas.eyebrow;
  const mostrarVerTodo = pres.mostrar_ver_todo !== false;

  const gridClase =
    variante === 'lista'
      ? 'sp-grid-productos sp-grid-productos--lista'
      : variante === 'compacto'
        ? 'sp-grid-productos sp-grid-productos--compacto'
        : 'sp-grid-productos';

  let cuerpo;
  if (variante === 'destacado') {
    const [principal, ...resto] = seleccion;
    cuerpo = `
      <div class="sp-productos-destacado">
        <div class="sp-productos-destacado__principal">${tarjetaProductoHtml(principal, tienda.empresa.moneda, estado)}</div>
        ${resto.length ? `<div class="sp-grid-productos">${resto.map((p) => tarjetaProductoHtml(p, tienda.empresa.moneda, estado)).join('')}</div>` : ''}
      </div>`;
  } else {
    cuerpo = `<div class="${gridClase}">${seleccion.map((producto) => tarjetaProductoHtml(producto, tienda.empresa.moneda, estado)).join('')}</div>`;
  }

  return `
    <section class="sp-seccion sp-productos sp-productos--${variante}">
      <div class="sp-contenedor">
        <div class="sp-seccion__cabecera">
          <div>
            <p class="sp-eyebrow">${escaparHtml(eyebrow)}</p>
            <h2>${escaparHtml(titulo)}</h2>
          </div>
          ${mostrarVerTodo ? '<a class="sp-seccion__ver-todo" href="/catalogo">Ir al catálogo</a>' : ''}
        </div>
        ${cuerpo}
      </div>
    </section>
  `;
}

function bloqueTestimonios(tienda, bloque, estado) {
  const items = bloque.configuracion?.items ?? [];
  if (items.length === 0) return '';
  // Filtrar por visibilidad individual: solo items con visible !== false
  const itemsVisibles = items.filter((item) => item.visible !== false);
  if (itemsVisibles.length === 0) return '';
  const pres = estado?.editor?.testimonios ?? {};
  const variante = pres.variante || 'tarjetas';
  const titulo = pres.titulo || 'Testimonios';

  const figura = (item, extra = '') => `
                <figure class="sp-testimonio${extra ? ` ${extra}` : ''}">
                  <span class="sp-testimonio__estrella" aria-hidden="true">${iconoLucide('Star')}</span>
                  <blockquote>${escaparHtml(item.descripcion || item.titulo || '')}</blockquote>
                  <figcaption>${escaparHtml(item.nombre || item.titulo || 'Cliente')}${item.cargo ? ` — ${escaparHtml(item.cargo)}` : ''}</figcaption>
                </figure>`;
  const visibles = itemsVisibles.slice(0, 4);

  let cuerpo;
  if (variante === 'central') {
    const [principal, ...resto] = visibles;
    cuerpo = `
        <div class="sp-testimonios-c">
          <div class="sp-testimonios-c__principal">${figura(principal, 'sp-testimonio--principal')}</div>
          <div class="sp-testimonios-c__resto">${resto.map((i) => figura(i)).join('')}</div>
        </div>`;
  } else if (variante === 'minimal') {
    cuerpo = `
        <div class="sp-testimonios-m">${visibles.map((i) => figura(i, 'sp-testimonio--minimal')).join('')}</div>`;
  } else if (variante === 'carrusel') {
    cuerpo = `
        <div class="sp-testimonios-carrusel">${visibles.map((i) => figura(i)).join('')}</div>`;
  } else {
    cuerpo = `
        <div class="sp-testimonios">${visibles.map((i) => figura(i)).join('')}</div>`;
  }

  return `
    <section class="sp-seccion sp-testimonios-seccion sp-testimonios-seccion--${variante}">
      <div class="sp-contenedor">
        <div class="sp-seccion__cabecera">
          <div>
            <p class="sp-eyebrow">Clientes felices</p>
            <h2>${escaparHtml(titulo)}</h2>
          </div>
        </div>
        ${cuerpo}
      </div>
    </section>
  `;
}

function bloqueContacto(tienda, estado) {
  const urlWhatsApp = enlaceWhatsApp(tienda);
  if (urlWhatsApp === '/contacto' || urlWhatsApp.startsWith('/contacto')) return '';
  const pres = estado?.editor?.contacto ?? {};
  const variante = pres.variante || 'resumen';
  const titulo = pres.titulo || '¿Tienes una duda antes de comprar?';
  const texto =
    pres.texto ||
    (tienda.perfil.direccion ?? 'Escríbenos y te orientamos con disponibilidad, tallas o envíos.');
  const boton = `
        <a class="sp-boton sp-boton--primario" href="${urlWhatsApp}" target="_blank" rel="noreferrer">
          ${iconoRedSocial('whatsapp')}<span>Escribir por WhatsApp</span>
        </a>`;

  let cuerpo;
  if (variante === 'centrado') {
    cuerpo = `
      <div class="sp-contenedor sp-contacto-centrado">
        <p class="sp-eyebrow">Atención directa</p>
        <h2>${escaparHtml(titulo)}</h2>
        <p>${escaparHtml(texto)}</p>
        ${boton}
      </div>`;
  } else if (variante === 'tarjeta') {
    cuerpo = `
      <div class="sp-contenedor">
        <div class="sp-contacto-tarjeta">
          <div>
            <p class="sp-eyebrow">Atención directa</p>
            <h2>${escaparHtml(titulo)}</h2>
            <p>${escaparHtml(texto)}</p>
          </div>
          ${boton}
        </div>
      </div>`;
  } else {
    cuerpo = `
      <div class="sp-contenedor sp-contacto-resumen__contenido">
        <div>
          <p class="sp-eyebrow">Atención directa</p>
          <h2>${escaparHtml(titulo)}</h2>
          <p>${escaparHtml(texto)}</p>
        </div>
        ${boton}
      </div>`;
  }

  return `
    <section class="sp-seccion sp-contacto-resumen sp-contacto-resumen--${variante}">
      ${cuerpo}
    </section>
  `;
}

/**
 * HTML de la portada según `estado.seccion_orden` (banner, categorías,
 * propuesta de valor, testimonios y bloques de productos) más el resumen de
 * contacto. Es la MISMA construcción que usa el storefront en `vistaHome` y
 * que el preview del panel reutiliza, de modo que EDITOR = PREVIEW =
 * TIENDA PÚBLICA.
 * @param {object} tienda
 * @param {object} estado
 * @returns {string}
 */
export function contenidoHomeHtml(tienda, estado) {
  const bloquesPorTipo = new Map();
  for (const bloque of tienda.bloques) {
    if (!bloquesPorTipo.has(bloque.tipo)) bloquesPorTipo.set(bloque.tipo, []);
    bloquesPorTipo.get(bloque.tipo).push(bloque);
  }

  const secciones = estado.seccion_orden
    .map((codigo) => {
      const bloque = bloquesPorTipo.get(codigo)?.[0];
      // Respetar visibilidad del bloque: solo renderizar si está activo
      if (!bloque || bloque.estado !== 'activo') return '';
      switch (codigo) {
        case 'banner_principal':
          return bloqueBanner(tienda, bloque, estado);
        case 'categorias':
          return bloqueCategorias(tienda, bloque, estado);
        case 'propuesta_valor':
          return bloquePropuestaValor(tienda, bloque, estado);
        case 'testimonios':
          return bloqueTestimonios(tienda, bloque, estado);
        case 'footer':
          return '';
        default: {
          if (SECCIONES_PRODUCTOS.has(codigo)) {
            return bloqueProductos(tienda, bloque, codigo, estado);
          }
          return '';
        }
      }
    })
    .filter((html) => html);

  const hero = secciones[0] ?? '';
  const resto = secciones.slice(1).join('');

  return (
    `${hero}${resto}${bloqueContacto(tienda, estado)}` ||
    `
    <section class="sp-page sp-contenedor sp-vacio">
      <span class="sp-vacio__icono" aria-hidden="true">${iconoLucide('ShoppingBag')}</span>
      <h1>Bienvenido a ${escaparHtml(tienda.empresa.nombre)}</h1>
      <p>Esta sección todavía no tiene contenido. Explora el catálogo para descubrir los productos.</p>
      <a class="sp-boton sp-boton--primario" href="/catalogo">Ver catálogo</a>
    </section>
  `
  );
}

export function vistaHome(contenedor, tienda, estado) {
  contenedor.innerHTML = contenidoHomeHtml(tienda, estado);

  conectarSliders(contenedor, window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  conectarBotonesAgregar(contenedor);
}
